import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  handleSpin,
  handleGetSpinParams,
  handleBuyFeature,
  handleGambleFreespins,
  handleCollectFreespins,
  handleRandomPotPresentation,
} from "../../service";
import { randomPotFlightDone } from "../../Games/Shared/randomPot";
import { initializeGame, refreshGameData } from "./gameSettingsSlice";
import { devSimulationSpinPatch } from "../../devSimulation";
import { Settings } from "../../settings";
import {
  COMMAND_KIND,
  kindOfCommand,
  kindOfSpinArg,
} from "../../utils/spinCommands";
import { fsDiag, fsBlockedBy } from "../../utils/fsDiag";

// ── The ban guard, in one place ──
//
// A refused command on a closed round answers 202; the SECOND one locks the
// session for good, `init` included (docs/api-contract-mad-labs-freespins.md
// §0). Both live-send thunks below share this test so neither can drift.
//
// As a `condition` the thunk never runs at all, so there is no request to
// cancel — the choke point is before the payload is even built.
const notWhileBlocked = (_arg, { getState }) => {
  const spin = getState().spin;
  const blocked = fsBlockedBy("rgsPlay.condition", [
    ["rgsPlayInFlight", spin.rgsPlayInFlight],
    ["playBlockedUntilResync", spin.playBlockedUntilResync],
    ["status === pending", spin.status === "pending"],
    ["featureSpinStatus === pending", spin.featureSpinStatus === "pending"],
  ]);
  return !blocked;
};

// Thunk for buying bonus feature
export const rgsBuyFeature = createAsyncThunk(
  "spin/rgsBuyFeature",
  async ({ level } = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet } = state.game;

    try {
      const result = await handleBuyFeature(bet, level);
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to buy feature");
    }
  },
  // ── The same choke point rgsPlay carries, for the same reason ──
  //
  // Nothing in the UI dispatches this today — BuyBonusPopup buys through
  // handleSpin/rgsPlay with `purchasedFeature`, so it inherits rgsPlay's
  // guards. This thunk does not: RGS.buyFeature calls spin() directly, which
  // skips both the in-flight check and the available_actions check, so a
  // second dispatch while the first is in the air sends a second live `spin`
  // to a round the server may have already closed. That is the 202, and two
  // of those is a ban.
  //
  // Guarded rather than deleted: it is exported, it is wired into
  // gameSettingsSlice's reducers, and the next person to need a buy button
  // will find it before they find this comment.
  { condition: notWhileBlocked },
);

// Thunk for spinning the reels
export const rgsPlay = createAsyncThunk(
  "spin/rgsPlay",
  async (options = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet, token, featureState } = state.game;
    const freeRoundsActive = state.freeRounds?.status === "active";
    const freeRoundsBet = state.freeRounds?.betValue;
    const availableActions = state.game?.availableActions || ["init", "spin"];
    const {
      purchasedFeature,
      purchasedLevel,
      bonusCost,
      gambleAction,
      freeSpin,
      respin,
      randomPotPresentation,
    } = options;

    try {
      // ── The Random Pot's presentation spin (Games/Shared/randomPot) ──
      // SENDS NOTHING — the provider answers from the trigger response it
      // kept, with the awarded coins on the board. Checked FIRST, before every
      // guard below: flow.state is `respin` here, and those guards exist for
      // what reaches the server, which this does not. The answer waits for the
      // logo's flight, so the reels stop once it is back.
      if (randomPotPresentation) {
        await randomPotFlightDone();
        const result = await handleRandomPotPresentation();
        return { ...result, command: "spin" };
      }

      // ── One free spin ──
      //
      // GUARDED, and the guard is the whole point. Sending `freespin` against
      // a round the server has already closed returns 202, and a SECOND
      // rejected action locks the session permanently — `init` included. That
      // is not theoretical: it happened on 2026-08-17 while capturing the
      // round contract. See docs/api-contract-mad-labs-freespins.md §0.
      if (freeSpin) {
        fsDiag("rgsPlay freespin -> sending", { availableActions });
        if (!availableActions.includes("freespin")) {
          fsDiag("rgsPlay freespin REFUSED LOCALLY: not in available_actions", {
            availableActions,
          });
          // The API's own shape, so callers branch on it exactly as they do
          // for a real refusal.
          return rejectWithValue({
            errors: [{ code: 202, desc: "unavailable_action" }],
          });
        }
        const result = await handleSpin(bet, token, "freespin", undefined, undefined);
        return { ...result, command: "freespin" };
      }

      // ── One Hold & Win respin (Cleopatra's Fire) ──
      //
      // Explicit, like `freespin` above, and guarded the same way: only when
      // the server OFFERS it. getSpinParams picks `respin` only for Zeus's
      // featureState shape, which this game's H&W does not produce — and a
      // respin sent after the round settled is a 202, two of which lock the
      // session (the Zeus ban). Stricter than the base guard on purpose: no
      // list, no respin.
      if (respin) {
        if (!Array.isArray(availableActions) || !availableActions.includes("respin")) {
          return rejectWithValue({
            errors: [{ code: 202, desc: "unavailable_action" }],
          });
        }
        const result = await handleSpin(bet, token, "respin", undefined, undefined);
        return { ...result, command: "respin" };
      }

      // ── Free-spin gamble (GDD §9) ──
      //
      // Routed through rgsPlay rather than given its own thunk on purpose.
      // `gamble_freespins` and `collect_freespins` come back as ordinary spin
      // responses — balance, flow.state, available_actions, features — so
      // every reducer already listening to rgsPlay.fulfilled handles them with
      // no new cases. A separate thunk would mean teaching each of those
      // slices about a second action for the same payload.
      //
      // The provider methods, NOT handleSpin: they carry the
      // available_actions guard, and sending an action the server did not
      // offer is what got Zeus players banned on this operator.
      if (gambleAction) {
        const result =
          gambleAction === "gamble_freespins"
            ? await handleGambleFreespins()
            : await handleCollectFreespins();
        return { ...result, command: gambleAction };
      }

      // Get command and spinOptions from RGS plugin based on universal state
      const { command, spinOptions } = handleGetSpinParams({
        bet,
        featureState,
        freeRoundsActive,
        freeRoundsBet,
        availableActions,
        purchasedFeature,
        purchasedLevel,
      });

      // ── Never send a command the round did not offer ──
      //
      // The same guard `freespin` above and RGS.gambleAction already carry, and
      // for the same reason: this operator answers an unoffered action with 202,
      // and TWO rejected actions lock the session permanently — `init` included.
      // See docs/api-contract-mad-labs-freespins.md §0.
      //
      // `spin` had no such guard, which left a real hole. While flow.state is
      // `gamble_freespins` the offered actions are the two gamble ones, so any
      // path that hands the player a live spin button mid-trigger could burn the
      // session in two clicks — the failed-collect case in GambleDialogController
      // being the one that actually reached it.
      //
      // Deliberately permissive when the list is absent or empty: that means we
      // have not been told, and refusing on no information would dead-button a
      // legitimate spin. Only an explicit list that omits the command refuses.
      if (
        Array.isArray(availableActions) &&
        availableActions.length > 0 &&
        !availableActions.includes(command)
      ) {
        return rejectWithValue({
          errors: [{ code: 202, desc: "unavailable_action" }],
        });
      }

      // Response is already transformed by RGS class
      const result = await handleSpin(
        bet,
        token,
        command,
        purchasedFeature,
        spinOptions,
      );

      // ── DEV SIMULATION: the half a reducer cannot fake ──
      //
      // The simulation replaces `state.currentRound` inside gameSettingsSlice,
      // which every OTHER slice never sees. freeSpinsSlice is the one that
      // matters: it sets `triggered` from this payload, and that flag is what
      // decides between ending on the scatters and cycling for ever.
      //
      // Patched HERE so `round` and `freeSpinsTrigger` reach every listener in
      // one consistent response, exactly as a real trigger does. Returns null
      // whenever the simulation is off, so the live payload is untouched and
      // this costs a single comparison in production.
      const simPatch = devSimulationSpinPatch();
      if (simPatch) {
        return { ...result, ...simPatch, command, bonusCost };
      }

      // Return result with command and bonusCost included (already normalized by RGS)
      return { ...result, command, bonusCost };
    } catch (error) {
      // If error is an object with errors array (API error), return full error object
      // Otherwise return error message
      if (
        error &&
        typeof error === "object" &&
        error.errors &&
        Array.isArray(error.errors)
      ) {
        return rejectWithValue(error);
      } else {
        return rejectWithValue(error.message || "Failed to spin the reels");
      }
    }
  },
  {
    // ── One request in flight at a time ──
    //
    // Ported from `main` (commit e7230589), which this branch predates. Players
    // were BANNED for sending a second command 173-542ms after a legitimate
    // one: the server had already closed the round, answered
    // `202 unavailable_action`, and a second refusal is `104 locked` — session
    // dead, `init` included.
    //
    // The specific UI paths are guarded individually, but any future one would
    // reintroduce the risk, so the duplicate is stopped at the single point
    // every command passes through.
    //
    // rgsPlayInFlight is the authoritative check — set ONLY by rgsPlay.pending,
    // cleared ONLY by fulfilled/rejected. status/featureSpinStatus are a second
    // layer and are NOT reliable alone: setFeatureSpinComplete(true) is shared
    // cleanup used by 15+ unrelated call sites and its reducer forces
    // featureSpinStatus to "succeeded" even while a request is still pending —
    // verified live on main, a real second `respin` reached the API 3ms after
    // the first with the old guard reading "succeeded".
    //
    // ── A failed feature command is never retried ──
    //
    // After a refusal our state provably disagrees with the server, so ANY
    // command built from it may be illegal. Nothing else stops the retry:
    // gameSettingsSlice's rejected case leaves availableActions untouched, the
    // free-spin loop keeps `remaining > 0`, and the reels still stop — so the
    // loop simply spins again. We refuse to send anything until a real re-sync
    // (initializeGame / refreshGameData) has happened. The error-recovery flow
    // performs exactly that, and a page reload re-inits, so this cannot wedge
    // the game shut.
    //
    // WIDER THAN MAIN, deliberately. Main scoped the block to respins. This
    // branch also has `freespin` and the two gamble decisions, and both of
    // their ban paths are live here: a rejected freespin is retried by the
    // round loop, and a rejected gamble re-arms both dialog buttons. So the
    // block is armed for FEATURE and DECISION alike. Base-game errors
    // (not_enough_money, bet limits) must stay retryable and are not blocked,
    // exactly as on main.
    //
    // NOT COVERED, on purpose: two tabs on one session. Each tab is its own
    // client with its own copy of this guard, so each can still send one
    // command from stale state. Main verified the other PopiPlay games behave
    // identically; a cross-tab lock was built there and deliberately removed.
    // That race belongs to the server.
    condition: notWhileBlocked,
  },
);

// Initial state for the spin slice
const initialState = {
  isSpinning: false, // Indicates if a spin is currently in progress
  status: "idle", // Tracks the status of the API request
  error: null, // Stores any error messages
  manualStop: false, // Flag to track if the spin was manually stopped
  spinComplete: true, // Indicates if the spin has completed
  windupComplete: false, // Indicates if all reels have completed their windup phase
  windupCompletedCount: 0, // Tracks how many reels have completed windup (must reach REEL_COUNT)
  reelsFrozen: false, // Flag to track if reels were frozen due to error (need recovery)
  // Set ONLY by rgsPlay.pending, cleared ONLY by rgsPlay.fulfilled/rejected.
  // Nothing else may write this — it exists so the duplicate-request guard
  // above cannot be defeated by unrelated cleanup dispatches (see the
  // condition). Do not repurpose for UI state.
  rgsPlayInFlight: false,
  // True after a FEATURE or DECISION command was refused: our state disagrees
  // with the server, so nothing further may be sent until a successful re-sync
  // clears it. Set only by rgsPlay.rejected, cleared only by
  // initializeGame.fulfilled / refreshGameData.fulfilled. Named for the whole
  // family, not for respins — freespin and the gamble ride the same guard.
  playBlockedUntilResync: false,
  // Feature game states (generic for any feature with parallel spin cycle)
  featureSpinActive: false, // Indicates if feature spin is in progress
  featureSpinStatus: "idle", // Tracks the status of feature spin API request
  featureSpinComplete: true, // Indicates if feature spin has completed
  featureSpinManualStop: false, // Flag to track if feature spin was manually stopped
};

// Create a slice for spin-related state and actions
const spinSlice = createSlice({
  name: "spin",
  initialState,
  reducers: {
    // Action to manually stop a spin
    manualSpinStop: (state) => {
      if (state.isSpinning && !state.manualStop) {
        state.manualStop = true; // Always mark as manual stop when requested
      }
    },

    // Action to manually set spinning state
    manualSpinStart: (state) => {
      state.isSpinning = true; // Set isSpinning
      state.spinComplete = false; // Set spinComplete
    },

    // Action to set the spin completion status
    setSpinComplete: (state, action) => {
      // Allow setting spinComplete for both succeeded and failed status
      // Failed status occurs with error 205 (free rounds) where reels still complete their animation
      if (state.status === "succeeded" || state.status === "failed") {
        state.isSpinning = false; // Mark spinning as false
        state.spinComplete = action.payload; // Update spin completion status
      }
    },

    // Action to mark reels as frozen (need recovery)
    setReelsFrozen: (state, action) => {
      state.reelsFrozen = action.payload;
    },

    // Action to set the windup completion status
    setWindupComplete: (state, action) => {
      state.windupComplete = action.payload; // Update windup completion status
    },

    // CRITICAL: Increment windup counter - called by each reel when it completes windup
    // Only sets windupComplete to true when ALL reels have completed
    incrementWindupCount: (state) => {
      state.windupCompletedCount += 1;

      // Only set windupComplete when ALL reels have finished windup
      if (state.windupCompletedCount === Settings.REEL_COUNT) {
        state.windupComplete = true;
      }
    },

    // Reset windup counter - called when a new spin starts
    resetWindupCount: (state) => {
      state.windupCompletedCount = 0;
      state.windupComplete = false;
    },

    // Feature game actions (generic for any feature with parallel spin cycle)
    // Action to set feature spin active state
    setFeatureSpinActive: (state, action) => {
      state.featureSpinActive = action.payload;
    },

    // Action to manually stop a feature spin
    featureSpinManualStop: (state) => {
      if (state.featureSpinActive && !state.featureSpinManualStop) {
        state.featureSpinManualStop = true;
      }
    },

    // Action to set feature spin completion status
    // Handles both boolean and object payloads: dispatch(setFeatureSpinComplete(true))
    // or dispatch(setFeatureSpinComplete({ complete: true, bet }))
    setFeatureSpinComplete: (state, action) => {
      const complete = typeof action.payload === 'boolean'
        ? action.payload
        : action.payload?.complete ?? action.payload;

      // When completing (true), always allow — exit cleanup needs this to work
      // regardless of featureSpinStatus. When starting (false), guard on status.
      if (complete) {
        state.featureSpinActive = false;
        state.featureSpinComplete = true;
        state.featureSpinStatus = "succeeded"; // Sync status
      } else if (state.featureSpinStatus === "succeeded") {
        state.featureSpinActive = false;
        state.featureSpinComplete = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ── The bonus buy shares the in-flight flag ──
      //
      // rgsBuyFeature is a LIVE `spin` (RGS.buyFeature calls spin() directly),
      // so it has to raise and clear the same flag rgsPlay raises, or the
      // condition it now carries has nothing to read and a double-tap sends
      // two. Nothing else here reacts to it: the round itself arrives through
      // rgsPlay when the UI buys the normal way, and gameSettingsSlice already
      // owns the fulfilled/rejected handling for this thunk.
      .addCase(rgsBuyFeature.pending, (state) => {
        state.rgsPlayInFlight = true;
      })
      .addCase(rgsBuyFeature.fulfilled, (state) => {
        state.rgsPlayInFlight = false;
      })
      .addCase(rgsBuyFeature.rejected, (state) => {
        state.rgsPlayInFlight = false;
      })
      .addCase(rgsPlay.pending, (state, action) => {
        // Authoritative in-flight flag for the condition guard. Set for EVERY
        // kind, above the decision early-return below — a gamble is a command
        // like any other and must not be sendable twice.
        state.rgsPlayInFlight = true;

        // ── Judge by the COMMAND, not only by the flag ──
        //
        // featureSpinActive is set by a previous `fulfilled`, so it cannot
        // describe the FIRST request of a sequence: the first `freespin` of a
        // round arrives before any response could raise the flag, and was
        // being mistaken for a base spin — flipping base isSpinning, which
        // the win controller reads as "a new spin started" and answers by
        // clearing the dim and every win animation still on the board.
        //
        // meta.arg is the options object rgsPlay was dispatched with, so the
        // intent is known here rather than inferred a response too late.
        // What each kind means — and why a decision touches nothing in all
        // three handlers — lives with the classifier in utils/spinCommands.
        const argKind = kindOfSpinArg(action.meta?.arg);
        if (argKind === COMMAND_KIND.DECISION) return;

        const isFeatureSpin =
          state.featureSpinActive || argKind === COMMAND_KIND.FEATURE;

        if (isFeatureSpin) {
          // Update feature spin states
          state.featureSpinStatus = "pending";
          state.featureSpinComplete = false;
          state.featureSpinManualStop = false;
        } else {
          // Update regular game states
          state.error = null;
          state.status = "pending";
          state.isSpinning = true;
          state.spinComplete = false;
          state.windupComplete = false;
          state.windupCompletedCount = 0; // Reset counter for new spin
          state.manualStop = false;
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        state.rgsPlayInFlight = false;
        fsDiag("rgsPlay fulfilled", {
          command: action.payload?.command,
          flowState: action.payload?.flowState,
          availableActions: action.payload?.availableActions,
          // The ladder as the SERVER states it, before anything here reads it.
          // The room is derived from fsLevel and nothing else, so a room that
          // looks wrong is either this number or the derivation — and this
          // says which.
          fsLevel: action.payload?.features?.fs_state?.level,
          collected:
            action.payload?.features?.fs_state?.total_collected_scatters,
          // gameMode decides whether gameSettingsSlice writes this round into
          // the BASE game (its isFeatureSpin test). A `freespin` step reading
          // "base" is the fault that let a replayed free spin leak onto the
          // base board once the room closed. Printed so one log answers it.
          gameMode: action.payload?.gameMode,
          // The round's OWN pay, not the accumulator. A freespin step showing
          // the running total instead is what jammed the replay loop.
          roundWin: action.payload?.round?.win,
        });

        // Use command only: a FEATURE spin is played on its own board with its
        // own round, so the base game's isSpinning/spinComplete must not move.
        // `spin` is base game (free rounds included) and does touch them.
        //
        //   respin    Pot Collection
        //   freespin  Mad Labs free spins — same separation, same reason.
        //             Without it every free spin looked like a base spin:
        //             isSpinning flipped on the base state, and the base win
        //             display and win controller both reacted to a round that
        //             was never theirs.
        const kind = kindOfCommand(action.payload.command);

        // A decision (the free-spins gamble) reels nothing and must leave
        // both spin states exactly as it found them — touching either is
        // what wiped the win effects the moment PLAY was pressed. An
        // UNCLASSIFIED command gets the same treatment: defaulting it to
        // base is how every state leak so far began.
        if (kind !== COMMAND_KIND.BASE && kind !== COMMAND_KIND.FEATURE) {
          return;
        }

        const wasFeatureSpin = state.featureSpinActive;

        if (kind === COMMAND_KIND.FEATURE) {
          state.featureSpinStatus = "succeeded";
          state.featureSpinActive = true;
          state.featureSpinComplete = false;
          state.featureSpinManualStop = false;

          if (wasFeatureSpin) {
            state.isSpinning = false;
            state.status = "idle";
            state.spinComplete = true;
          } else {
            state.status = "succeeded";
            // Do NOT set isSpinning or spinComplete - reel flow handles them
          }
        } else {
          // Spin command: base game or free rounds - same isSpinning logic
          state.status = "succeeded";
          state.featureSpinActive = false;
          state.featureSpinStatus = "idle";
          state.featureSpinComplete = true;
          // Do NOT set isSpinning or spinComplete - reel flow handles them
        }
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        state.rgsPlayInFlight = false;
        fsDiag("rgsPlay REJECTED", {
          argKind: kindOfSpinArg(action.meta?.arg),
          payload: action.payload,
        });

        // Determine which spin type failed — by intent, same as `pending`.
        const argKind = kindOfSpinArg(action.meta?.arg);

        // ── The block, armed before anything else ──
        //
        // A refused FEATURE or DECISION command means the server has moved on
        // without us. Both are armed here, ABOVE the decision early-return
        // below, because the gamble's ban path is real: its dialog re-arms
        // both buttons on a failure and the second press is the second
        // offence. A base-game refusal stays retryable.
        if (argKind === COMMAND_KIND.FEATURE || argKind === COMMAND_KIND.DECISION) {
          state.playBlockedUntilResync = true;
        }

        // A refused gamble touches neither spin state: GambleDialogController
        // unwraps the thunk and handles it itself.
        if (argKind === COMMAND_KIND.DECISION) return;

        if (state.featureSpinActive || argKind === COMMAND_KIND.FEATURE) {
          // A feature spin dispatched with no options while a feature owns the
          // round reads as argKind null, which is why the flag is tested too —
          // this is main's original test, kept.
          state.playBlockedUntilResync = true;
          // Update feature spin states
          state.featureSpinStatus = "failed";
          state.featureSpinActive = false;
        } else {
          // Update regular game states
          state.status = "failed";
          state.isSpinning = false;
          state.error = action.payload || "Spin failed";
          // spinComplete will be set by ReelController when reels finish stopping
        }
      })
      // Handle feature completion reload from initializeGame
      .addCase(initializeGame.fulfilled, (state, action) => {
        // Fresh server truth — safe to send commands again.
        state.playBlockedUntilResync = false;

        const { result } = action.payload;
        const { reloadState } = result;

        // Check if we have any feature completion reload from RGS
        if (reloadState?.needsReload && reloadState.phase === "completion") {
          // Set feature spin state to completed (allows dialog trigger effect to fire)
          state.featureSpinStatus = "succeeded";
          state.featureSpinActive = false;
          state.featureSpinComplete = true;
          state.featureSpinManualStop = false;
        }
      })
      // Re-sync after an error (ErrorRecoveryOverlay / WarningPopup dismiss)
      // replaced featureState + availableActions with server truth, so the
      // block is released. This is the ONLY other way out of it, and it is why
      // the guard cannot wedge the game shut.
      .addCase(refreshGameData.fulfilled, (state) => {
        state.playBlockedUntilResync = false;
      })
      // ── Leaving a replay releases it too ──
      //
      // A replay STEP that fails to download arms this block above, because
      // the reducer sees a refused FEATURE command and cannot tell a recorded
      // round from a live one. It says nothing about the live session — that
      // session never sent anything while the recording was on screen.
      //
      // The valve matters now that refreshGameData is itself refused during a
      // replay (see notWhileReplaying in gameSettingsSlice): without this, a
      // replay whose step 404'd would leave the player's LIVE game blocked
      // after they closed the History board, with the flag's only two other
      // release paths already behind them.
      //
      // Matched by action-type string, not by importing endReplaySession:
      // replaySlice imports rgsPlay from this file, so the import would be a
      // cycle. Same reason freeSpinsSlice matches these two by string.
      .addCase("replay/endReplaySession", (state) => {
        state.playBlockedUntilResync = false;
        // ── And the in-flight flag ──
        //
        // A replay STEP is dispatched through rgsPlay like any spin, so it
        // raises this. Closing the History board mid-step means the thunk that
        // would clear it belongs to a session that no longer exists — and
        // nothing else on earth clears this flag, by design. The live game
        // would then refuse every spin for ever, with no error and no popup.
        //
        // Nothing live can be in flight at this moment: while replayMode was on
        // the RGS object short-circuited every command to _replaySpin, so there
        // is no real request this could be abandoning.
        state.rgsPlayInFlight = false;
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectIsSpinning = (state) => state.spin.isSpinning;
export const selectSpinStatus = (state) => state.spin.status;
export const selectSpinError = (state) => state.spin.error;
export const selectManualStop = (state) => state.spin.manualStop;
export const selectSpinComplete = (state) => state.spin.spinComplete;
export const selectWindupComplete = (state) => state.spin.windupComplete;
export const selectReelsFrozen = (state) => state.spin.reelsFrozen;
export const selectFeatureSpinActive = (state) =>
  state.spin.featureSpinActive;
export const selectFeatureSpinStatus = (state) => state.spin.featureSpinStatus;
export const selectFeatureSpinComplete = (state) =>
  state.spin.featureSpinComplete;
export const selectFeatureSpinManualStop = (state) =>
  state.spin.featureSpinManualStop;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  manualSpinStop,
  manualSpinStart,
  setSpinComplete,
  setWindupComplete,
  incrementWindupCount,
  resetWindupCount,
  setReelsFrozen,
  setFeatureSpinActive,
  featureSpinManualStop,
  setFeatureSpinComplete,
} = spinSlice.actions;

// Export the reducer to be included in the store
export default spinSlice.reducer;
